import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import RoutedLayout from 'components/Layout/RoutedLayout';
import { Button, Form, Input, Select } from 'antd';
import ModalForm from 'components/ModalForm';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { EDIT_CUSTOMER_DASHBOARD } from 'shared/constants/permissions';
import useUser from 'hooks/useUser';
import useCustomer from 'hooks/useCustomer';
import useCheckPermission from 'hooks/useCheckPermission';
import { getDashboardRoutes } from 'components/Dashboard/utils';

const CREATE_CUSTOMER_DASHBOARD_MUTATION = gql`
  mutation CreateCustomerDashboard($customerId: ID!, $input: DashboardInput!) {
    createCustomerDashboard(customerId: $customerId, input: $input) {
      id
    }
  }
`;

const CREATE_PERSONAL_DASHBOARD_MUTATION = gql`
  mutation CreatePersonalDashboard($customerId: ID!, $input: DashboardInput!) {
    createPersonalDashboard(customerId: $customerId, input: $input) {
      id
    }
  }
`;

const Dashboards = () => {
  const { customer, loading, refetch, hasCustomerPermission } = useCustomer({ extraFields: 'dashboardPages { id title user { id } dashboards { id title parent settings } }' });
  const [createCustomerDashboard] = useMutation(CREATE_CUSTOMER_DASHBOARD_MUTATION);
  const [createPersonalDashboard] = useMutation(CREATE_PERSONAL_DASHBOARD_MUTATION);
  const [createDashboardOpen, setCreateDashboardOpen] = useState(false);
  const history = useHistory();
  const { url } = useRouteMatch();
  const { user } = useUser();
  const checkPermission = useCheckPermission();

  const userDashboardPage = customer?.dashboardPages?.find(d => d.user?.id === user?.id);
  const customerDashboardPage = customer?.dashboardPages?.find(d => !d.user);

  const routes = [{
    path: 'personal',
    label: 'Personal Dashboards',
    emptyLabel: 'No personal dashboards',
    link: false,
    children: getDashboardRoutes({
      dashboardPage: userDashboardPage,
      props: {
        onChange: () => refetch(),
        onDelete: () => history.push(url),
      },
      checkPermission,
    }),
  }, {
    path: 'account',
    label: 'Account Dashboards',
    emptyLabel: 'No account dashboards',
    link: false,
    children: getDashboardRoutes({
      dashboardPage: customerDashboardPage,
      props: {
        onChange: () => refetch(),
        onDelete: () => history.push(url),
      },
      checkPermission,
    }),
  }];

  const onCreateDashboard = async (values) => {
    const { type, ...input } = values;
    if (type === 'account') {
      const res = await createCustomerDashboard({ variables: { customerId: customer.id, input } });
      return { id: res.data.createCustomerDashboard.id, type: 'account' };
    }
    const res = await createPersonalDashboard({ variables: { customerId: customer.id, input } });
    return { id: res.data.createPersonalDashboard.id, type: 'personal' };
  };

  const onDashboardCreated = async (res) => {
    setCreateDashboardOpen(false);
    await refetch();
    history.push(`${url}/${res.type}/${res.id}`);
  };

  const afterMenu = (
    <div style={{ margin: 4, marginTop: 16 }}>
      <Button type="primary" block icon={<PlusOutlined />} onClick={() => setCreateDashboardOpen(true)}>Create Dashboard</Button>
      <ModalForm
        title="Create Dashboard"
        open={createDashboardOpen}
        onCancel={() => setCreateDashboardOpen(false)}
        onDone={onDashboardCreated}
        onSubmit={onCreateDashboard}
        okText="Create"
      >
        <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Title is required' }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Type" name="type" initialValue="personal">
          <Select options={[
            { label: 'Personal', value: 'personal' },
            { label: 'Account', value: 'account', disabled: !hasCustomerPermission(EDIT_CUSTOMER_DASHBOARD) },
          ]}
          />
        </Form.Item>
      </ModalForm>
    </div>
  );

  return (
    <RoutedLayout sidebar={{ afterMenu }} routes={routes} loading={loading} />
  );
};

export default Dashboards;
